import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    Skeleton,
    SkeletonBar,
} from 'wanderu-animations';
import {
    InputIndicators,
} from 'wanderu-component-form';
import {
    LocationPinIcon,
} from 'wanderu-components';
import {
    generateFakeOnChangeEvent,
} from 'wanderu-utils';

import styles from './Input.scss';

// TS: Need for widget b/c inferno do not have synthetic events
const KEY_MAP = {
    8: 'Backspace',
    13: 'Enter',
    27: 'Escape',
    38: 'ArrowUp',
    40: 'ArrowDown',
};
const DUMMY_SPACER = '-';

export default class Input extends React.Component {
    static propTypes = {
        autoClear: PropTypes.bool,
        autoComplete: PropTypes.string,
        autoFocus: PropTypes.bool,
        autoRestore: PropTypes.bool,
        className: PropTypes.string,
        clearable: PropTypes.bool,
        'data-id': PropTypes.string,
        inputClass: PropTypes.string,
        label: PropTypes.string,
        loading: PropTypes.bool,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onFocus: PropTypes.func,
        onKeyUp: PropTypes.func,
        placeholder: PropTypes.string,
        readOnly: PropTypes.bool,
        required: PropTypes.bool,
        showPin: PropTypes.bool,
        textClass: PropTypes.string,
        type: PropTypes.string,
        underline: PropTypes.bool,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    };

    static defaultProps = {
        autoComplete: '',
        label: '',
        onFocus() { },
        onKeyUp() { },
        readOnly: false,
        required: false,
        showPin: false,
        type: 'text',
    };

    constructor(props) {
        super(props);

        this.state = {
            isFirstFocus: true,
            isFirstInteraction: true,
            originalInput: this.props.value,
        };

        this.input = React.createRef();
    }

    componentDidMount() {
        if (this.input.current === document.activeElement) {
            this.onFocus();
        }
    }

    get shouldClear() {
        const { autoClear, readOnly } = this.props;
        return autoClear && !readOnly && this.state.isFirstInteraction;
    }

    get value() {
        if (!this.props.value) {
            return '';
        }

        return this.props.value;
    }

    get showClearIcon() {
        return this.props.clearable && this.value;
    }

    onFocus = (e) => {
        if (this.props.autoFocus && this.state.isFirstFocus) {
            return this.setState({ isFirstFocus: false });
        }

        if (this.props.autoRestore && this.state.isFirstFocus) {
            this.setState(() => ({ originalInput: this.props.value }));
        }

        this.props.onFocus(e);

        return undefined;
    }

    onKeyDown = (e) => {
        this.notifyParentOfInteraction();

        const key = KEY_MAP[e.which];
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            return this.preventCursorFromJumpingToStartOfInput(e);
        }

        if (key === 'Backspace' && this.shouldClear) {
            return this.clearInput(e);
        }

        if (key === 'Escape') {
            return this.input.current.blur();
        }

        return undefined;
    }

    preventCursorFromJumpingToStartOfInput = (e) => {
        return e.preventDefault();
    }

    clearInput = (e) => {
        const {
            onChange,
        } = this.props;
        const fakeEvent = generateFakeOnChangeEvent(e, '');
        onChange(fakeEvent);
    }

    onKeyUp = (e) => {
        const key = KEY_MAP[e.which];
        if (key === 'Enter') {
            return this.input.current.blur();
        }

        this.props.onKeyUp({ key });

        this.setInteracted();

        return undefined;
    }

    setInteracted() {
        this.setState({ isFirstInteraction: false });
    }

    notifyParentOfInteraction(e) {
        if (this.props.autoFocus && this.state.isFirstInteraction) {
            this.props.onFocus(e);
        }
    }

    onClick = (e) => {
        this.notifyParentOfInteraction(e);

        if (!this.shouldClear) {
            return;
        }

        this.clearInput(e);
        this.setInteracted();
    }

    onBlur = (e) => {
        e.persist();
        if (this.props.autoRestore && !this.props.value) {
            this.restoreInput(e, this.state.originalInput);
            return this.setState(
                () => ({ originInput: undefined }),
                // NB: Allow consumers to set state in response to the change event
                //    before emitting the blur event
                () => this.props.onBlur(e),
            );
        }
        this.props.onBlur(e);

        return undefined;
    }

    restoreInput(e, originalInput) {
        const fakeEvent = generateFakeOnChangeEvent(e, originalInput);
        this.props.onChange(fakeEvent);
    }

    render() {
        const {
            autoComplete,
            autoFocus,
            className,
            inputClass,
            label,
            loading,
            onChange,
            placeholder,
            readOnly,
            required,
            showPin,
            textClass,
            type,
            underline,
        } = this.props;

        return (
            <span
                className={classNames(
                    styles.container,
                    className,
                )}
            >
                {showPin && <LocationPinIcon className={styles.locationPin} />}
                {underline && (
                    <span className={styles.dummyContainer}>
                        <span className={classNames(
                            styles.dummyText,
                            { [textClass]: textClass },
                        )}>
                            {this.value
                                ? this.value
                                : DUMMY_SPACER}
                        </span>
                    </span>
                )}

                <input
                    aria-label={label}
                    aria-required={required}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    className={classNames(
                        styles.input,
                        inputClass,
                        {
                            [textClass]: textClass,
                            [styles.hideInput]: loading,
                        },
                    )}
                    data-id={this.props['data-id']}
                    onBlur={this.onBlur}
                    onChange={onChange}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    onKeyUp={this.onKeyUp}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    ref={this.input}
                    type={type}
                    value={this.value}
                />
                {loading && (
                    <Skeleton className={styles.skeleton}>
                        <SkeletonBar className={styles.skeletonBar} />
                    </Skeleton>
                )}
                {this.showClearIcon && (
                    <InputIndicators
                        onClearClick={(e) => {
                            this.clearInput(e);
                            this.input.current.focus();
                        }}
                        showClearIcon
                    />
                )}
            </span>
        );
    }
}
